<script setup>
// Overlay menu: the panel FLOATS ABOVE neighbouring content. For a panel that
// DISPLACES the content below it, use BaseAccordion / SidebarStage instead —
// there is no prop that turns one into the other.
//
// The panel is teleported to <body> by PrimeVue Popover, so no ancestor's
// `overflow` can clip it (that was bug #16: table row kebab menus cut off by
// BaseDataTable's scroll wrapper).
//
// Placement is Popover's job, not ours: it opens below the trigger and flips
// above when there is no room, left-aligned to the trigger and right-aligned
// when that would overflow the viewport. The old hand-rolled `align` matrix
// (left/right/top + md:* variants) was a manual stand-in for exactly this and
// has been removed along with `notAbsolute` / `withoutZIndex` / `withoutRelative`.
import { nextTick, onMounted, ref, watch } from "vue";
import Popover from "primevue/popover";

const props = defineProps({
    opened: {
        type: Boolean,
        default: false,
    },
    // Layout only. The panel's surface — background, border, radius, shadow and
    // inset — comes from the `popover` block in the BrandPreset, which points at
    // the same tokens Select's overlay uses so the two read as one system (#17).
    contentClass: {
        type: String,
        default: "flex flex-col gap-1",
    },

    closeOnTrigger: Boolean,
    contentClickOpened: Boolean,
});

const emit = defineEmits(["open"]);

const popover = ref(null);
const trigger = ref(null);
const panelMinWidth = ref(null);
const panelMaxHeight = ref(null);

// Open state is owned here rather than read back from Popover, whose `show`
// event fires from inside a <Transition> enter hook — too late and too fragile
// to be a component's source of truth. Popover's `hide` only syncs the paths it
// owns: outside click and Escape.
const open = ref(props.opened);
watch(open, () => emit("open", open.value));

// Popover.show() reads `event.currentTarget` without a null check, so the
// programmatic entry points hand it a synthetic event rather than null.
const triggerEvent = () => ({ currentTarget: trigger.value });

// Popover's gutter token (the gap it leaves between trigger and panel) plus a
// little breathing room off the viewport edge.
const PANEL_GUTTER = 10;
const PANEL_EDGE_MARGIN = 16;
// Never collapse the panel to nothing in a cramped container — below this it
// scrolls internally instead.
const PANEL_MIN_HEIGHT = 120;
// Below this much room under the trigger, opening downward stops being usable
// (roughly four list rows) and flipping up is the better trade.
const PANEL_USABLE_BELOW = 200;

const showPanel = () => {
    if (!trigger.value) return;

    // Reproduces the old panel's `min-w-full`: it was never narrower than its
    // trigger. Teleported to <body> a percentage width means nothing, so the
    // trigger's measured width is carried over instead.
    panelMinWidth.value = `${trigger.value.offsetWidth}px`;

    // Bound the panel's height, or Popover flips it above the trigger the moment
    // an async list outgrows the space below — and "above" is rarely empty space:
    // in a form it is the other fields, so the panel ends up covering them
    // (Save included). Prefer DOWNWARD AND SHRINK: cap to the room below so the
    // panel always fits there and long lists scroll inside it.
    //
    // Flipping up stays the fallback for when below is genuinely unusable — a
    // kebab on the last table row, where the page below the trigger is a few
    // pixels and the space above is empty page rather than form fields. Measuring
    // "is there anything up there worth covering" is not something this component
    // can do, so the usable-room threshold stands in for it.
    const rect = trigger.value.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - PANEL_GUTTER - PANEL_EDGE_MARGIN;
    const spaceAbove = rect.top - PANEL_GUTTER - PANEL_EDGE_MARGIN;
    const budget = spaceBelow >= PANEL_USABLE_BELOW ? spaceBelow : spaceAbove;
    panelMaxHeight.value = `${Math.max(budget, PANEL_MIN_HEIGHT)}px`;

    popover.value?.show(triggerEvent(), trigger.value);
};

const show = () => {
    // Opening one menu must close any other that is already open. The trigger's
    // own click is deliberately stopped — tables have clickable rows and must not
    // react to a kebab — so it never reaches the document listener PrimeVue
    // dismisses on. A synthetic document click does that job for every open
    // overlay; ours is not bound yet, so it cannot close us.
    document.dispatchEvent(new MouseEvent("click", { bubbles: true }));
    open.value = true;
    showPanel();
};

const hide = () => {
    open.value = false;
    popover.value?.hide();
};

const toggle = () => (open.value ? hide() : show());

const onHide = () => {
    open.value = false;
};

const onContentClick = () => {
    if (!props.contentClickOpened) {
        hide();
    }
};

onMounted(async () => {
    if (props.opened) {
        await nextTick();
        show();
    }
});

defineExpose({
    open: show,
    close: hide,
    toggle,
    opened: () => open.value,
});
</script>

<template>
    <div
        ref="trigger"
        class="dropdown">
        <div
            @keydown.enter="toggle"
            class="w-full h-full cursor-pointer"
            tabindex="0"
            @click.stop="toggle">
            <slot
                :open="open"
                name="trigger" />
        </div>

        <!-- `closeOnTrigger` means only the trigger may close the menu — no
             outside click, no Escape. Clicking inside the panel keeps it open by
             default in PrimeVue, which is what `contentClickOpened` asks for; the
             opposite case has to be closed by hand. -->
        <Popover
            ref="popover"
            :dismissable="!closeOnTrigger"
            :close-on-escape="!closeOnTrigger"
            :pt="{
                content: {
                    class: contentClass,
                    style: { minWidth: panelMinWidth, maxHeight: panelMaxHeight, overflowY: 'auto' },
                },
            }"
            @hide="onHide">
            <div @click="onContentClick">
                <slot name="content" />
            </div>
        </Popover>
    </div>
</template>
